import './common/dayjsConfig';

import React, { Suspense, useCallback } from 'react';
import Favicon from 'react-favicon';
import { useDispatch, useSelector } from 'react-redux';
import { Route, Switch, useLocation } from 'react-router';

import { isSingaporeCountry } from './common/isSingaporeCountry';
import useMarketingTrackingService from './common/tracking/useMarketingTrackingService';
import { RenderCompanyFullProfileAccessLimitReachedModal } from './components/CompanyFullProfileAccess';
import RenderCompanyVerificationModals from './components/CompanyVerificationModals/RenderCompanyVerificationModals';
import Drawer from './components/Drawer';
import ErrorBoundaryWithCrashReporting from './components/Error/ErrorBoundaryWithCrashReporting';
import { Footer } from './components/Footer';
import LoadingFullscreen from './components/LoadingFullscreen';
import LoadingModal from './components/LoadingModal';
import RenderJobSlotUpgradedModal from './components/Modal/JobSlotUpgradedModal/RenderJobSlotUpgradedModal';
import { MonetisationFixedInfoBanner } from './components/MonetisationFixedInfoBanner/MonetisationFixedInfoBanner';
import { MainHeaderHeightContext } from './context';
import GlintsMeta from './GlintsMeta';
import { CompanyReportStatusRedirect } from './modules/CompanyReportStatus/CompanyReportStatusRedirect';
import { SubscriptionAssistanceRequestModalProvider } from './modules/EmployerCommerce/SubscriptionPlanUpgrade/SubscriptionAssistanceRequestModal/SubscriptionAssistanceRequestModalProvider';
import GlintsChatWebsocketVisibilityHandler from './modules/GlintsChat/components/GlintsChatWebsocketVisibilityHandler';
import WSEventListenerWrapper from './modules/GlintsChat/components/WSEventListenerWrapper';
import { GlintsChatClientProvider } from './modules/GlintsChat/provider/GlintsChatClientProvider';
import Modal from './modules/Modal';
import {
  isSidebarVisible,
  Navbar,
  Sidebar,
  toggleSidebar,
} from './modules/Navigation';
import AlternativeNavbar from './modules/Navigation/AlternativeNavbar/AlternativeNavbar';
import useShowAlternativeHeader from './modules/Navigation/AlternativeNavbar/useShowAlternativeHeader';
import { NavigationProvider } from './modules/Navigation/NavigationProvider/Context';
import { LogoutBroadcastListener } from './modules/Session/LogoutBroadcastListener';
import RenderDesktopOnly from './modules/UseMobileAppPrompt/RenderDesktopOnly';
import { ZendeskVisibilityProvider } from './modules/Zendesk/ZendeskVisibilityProvider';
import MainRoutes from './routes';
import { getShowFeatureLoadingModal } from './selectors/features';

const RenderlessConfirmWhatsappNumber = React.lazy(
  () => import('./modules/User/RenderlessConfirmWhatsappNumber')
);
export const App = () => {
  const [headerHeight, setHeaderHeight] = React.useState(0);
  const [fixedBannerHeight, setFixedBannerHeight] = React.useState(0);
  const sidebarOpen = useSelector(isSidebarVisible);
  const isFeatureLoading = useSelector(getShowFeatureLoadingModal);
  // global listener for marketing tracking events, this listens for events sent via the emitter.
  useMarketingTrackingService({ listenToMarketingTrackingEvents: true });

  const dispatch = useDispatch();

  const handleDrawerOpenChange = useCallback(() => {
    dispatch(toggleSidebar());
  }, [dispatch]);

  const {
    alternativeHeaderContent,
    showAlternativeHeader,
    preventNavigation,
    setShowAlternativeHeader,
    alternativeSidebarContent,
    alternativeMobileContent,
  } = useShowAlternativeHeader();

  const location = useLocation();
  const isSGLandingRoute = location.pathname === '/' && isSingaporeCountry();

  return (
    <GlintsChatClientProvider>
      <WSEventListenerWrapper>
        <NavigationProvider
          value={{
            setShowAlternativeHeader,
          }}
        >
          <MainHeaderHeightContext.Provider
            value={headerHeight + fixedBannerHeight}
          >
            <GlintsChatWebsocketVisibilityHandler />
            <ZendeskVisibilityProvider>
              <SubscriptionAssistanceRequestModalProvider
                enableAutoTriggers={false}
              >
                <Favicon url="/images/favicon.png" />
                <GlintsMeta title="Glints - Career Discovery & Development Platform" />
                <Switch>
                  <Route path="*">
                    {isFeatureLoading && <LoadingModal />}
                    <RenderDesktopOnly>
                      <MonetisationFixedInfoBanner
                        setFixedBannerHeight={setFixedBannerHeight}
                      />
                    </RenderDesktopOnly>
                    <CompanyReportStatusRedirect />
                    <If condition={!isSGLandingRoute}>
                      <Choose>
                        <When condition={showAlternativeHeader}>
                          <AlternativeNavbar
                            setHeaderHeight={setHeaderHeight}
                            top={fixedBannerHeight}
                            canNavigate={!preventNavigation}
                            hasSidebar={Boolean(alternativeSidebarContent)}
                            mobileContent={alternativeMobileContent}
                          >
                            {alternativeHeaderContent}
                          </AlternativeNavbar>
                        </When>
                        <Otherwise>
                          <Navbar
                            setHeaderHeight={setHeaderHeight}
                            top={fixedBannerHeight}
                          />
                        </Otherwise>
                      </Choose>
                    </If>

                    <Drawer
                      sidebar={
                        <Sidebar
                          alternativeSidebarContent={alternativeSidebarContent}
                        />
                      }
                      position="right"
                      open={sidebarOpen}
                      onOpenChange={handleDrawerOpenChange}
                      headerHeight={headerHeight}
                      style={{ overflow: 'unset' }}
                    >
                      <div className="content">
                        <Suspense fallback={<LoadingFullscreen />}>
                          <MainRoutes />
                        </Suspense>
                      </div>
                      <Footer />
                      <Modal />
                      <LogoutBroadcastListener />
                      <RenderCompanyVerificationModals />
                      <ErrorBoundaryWithCrashReporting fallback={null}>
                        <Suspense fallback={null}>
                          <RenderCompanyFullProfileAccessLimitReachedModal />
                        </Suspense>
                      </ErrorBoundaryWithCrashReporting>
                      <ErrorBoundaryWithCrashReporting fallback={null}>
                        <Suspense fallback={null}>
                          <RenderlessConfirmWhatsappNumber />
                        </Suspense>
                      </ErrorBoundaryWithCrashReporting>
                      <ErrorBoundaryWithCrashReporting fallback={null}>
                        <Suspense fallback={null}>
                          <RenderJobSlotUpgradedModal />
                        </Suspense>
                      </ErrorBoundaryWithCrashReporting>
                    </Drawer>
                  </Route>
                </Switch>
              </SubscriptionAssistanceRequestModalProvider>
            </ZendeskVisibilityProvider>
          </MainHeaderHeightContext.Provider>
        </NavigationProvider>
      </WSEventListenerWrapper>
    </GlintsChatClientProvider>
  );
};
